/**
 * casingNav.ts — single source of truth for the casing vertical's navigation.
 *
 * Three surfaces consume this config:
 *   - Desktop sidebar   (HomeScreen.tsx / DesktopSidebar)  → items where surfaces includes 'sidebar'
 *   - Mobile bottom bar (BottomNav.tsx)                     → items where surfaces includes 'bottomBar'
 *   - Mobile More page  (MoreScreen.tsx)                    → items where surfaces includes 'more'
 *
 * Ordering per surface:
 *   - More  → array position (this file is authored in More order).
 *   - Bottom bar → the `bottomBarOrder` field (1-6). "More" is rendered by BottomNav itself.
 *   - Sidebar → grouped by `section` (section order below), items sorted by SIDEBAR_ORDER.
 *
 * A single linear array cannot satisfy both More order and the sidebar's within-section
 * order (e.g. Clients is first in the sidebar's OPERATIONS group but 8th in More), so the
 * sidebar's ordering lives here in SIDEBAR_ORDER / buildCasingSidebarGroups() rather than
 * being inferred from array position. Both surfaces therefore render exactly as before.
 *
 * `icon` is a lucide-react icon name as a string; each consumer maps it to a component.
 */

export type NavSurface = 'sidebar' | 'bottomBar' | 'more';
export type NavSection = 'CORE' | 'OPERATIONS' | 'SALES' | 'REPORTS' | 'FINANCE' | 'SYSTEM';

/** Sentinel subtitles telling MoreScreen to inject live/dynamic data. */
export type MoreSubtitleSentinel =
  | 'LIVE_PAYROLL'
  | 'LIVE_EQUIPMENT_COUNT'
  | 'LIVE_TEAM_COUNT'
  | 'LIVE_RATE_SHEET_COUNT';

export interface CasingNavItem {
  id: string;                          // unique slug, e.g. 'home', 'jobs'
  label: string;
  moreLabel?: string;                  // override label when rendered in More (e.g. 'Rate Sheets' vs 'Rates')
  icon: string;                        // lucide icon name as a string — consumers import from lucide-react
  section: NavSection;                 // for sidebar grouping
  surfaces: NavSurface[];              // which surfaces render this item
  path: string;                        // canonical path (used by sidebar + bottom bar)
  morePath?: string;                   // override path when rendered in More (e.g. invoices uses /invoices)
  moreSubtitle?: string | MoreSubtitleSentinel;
  bottomBarOrder?: number;             // 1-6 for bottom bar items
  gate?: string;                       // role gate expression as string, evaluated by consumer
}

/** Section render order for the desktop sidebar. */
export const SIDEBAR_SECTION_ORDER: NavSection[] = [
  'CORE',
  'OPERATIONS',
  'SALES',
  'REPORTS',
  'FINANCE',
  'SYSTEM',
];

/**
 * Full flat sidebar order (by id). Used to sort items within each section so the
 * sidebar renders identically regardless of the array's More-order authoring.
 */
export const SIDEBAR_ORDER: string[] = [
  // CORE
  'home', 'jobs', 'dispatch', 'tickets', 'safety', 'intake',
  // OPERATIONS
  'clients', 'equipment', 'team', 'clock-in', 'time-off', 'training',
  // SALES
  'sales-meetings', 'sales-pipeline',
  // REPORTS
  'reports',
  // FINANCE
  'payroll', 'timesheet-approvals', 'expenses', 'timesheet-reports', 'rates', 'quotes', 'invoices',
  // SYSTEM
  'ai-agent', 'alerts', 'settings',
];

/**
 * The nav item registry — authored in More display order (array position === More order).
 */
export const CASING_NAV: CasingNavItem[] = [
  // ── CORE (also the mobile bottom bar) ──
  { id: 'home', label: 'Home', icon: 'Home', section: 'CORE', surfaces: ['sidebar', 'bottomBar'], path: '/', bottomBarOrder: 1 },
  { id: 'jobs', label: 'Jobs', icon: 'ClipboardList', section: 'CORE', surfaces: ['sidebar', 'bottomBar'], path: '/jobs', bottomBarOrder: 2 },
  { id: 'dispatch', label: 'Dispatch', icon: 'Truck', section: 'CORE', surfaces: ['sidebar', 'bottomBar'], path: '/dispatch', bottomBarOrder: 3 },
  { id: 'tickets', label: 'Tickets', icon: 'Ticket', section: 'CORE', surfaces: ['sidebar', 'bottomBar'], path: '/tickets', bottomBarOrder: 4 },
  { id: 'safety', label: 'Safety', icon: 'Shield', section: 'CORE', surfaces: ['sidebar', 'bottomBar'], path: '/safety', bottomBarOrder: 5 },
  { id: 'intake', label: 'Intake', icon: 'Phone', section: 'CORE', surfaces: ['sidebar'], path: '/casing/intake' },

  // ── More order begins here ──
  { id: 'equipment', label: 'Equipment', icon: 'Wrench', section: 'OPERATIONS', surfaces: ['sidebar', 'more'], path: '/equipment', moreSubtitle: 'LIVE_EQUIPMENT_COUNT' },
  { id: 'shops', label: 'Shops', icon: 'Warehouse', section: 'OPERATIONS', surfaces: ['more'], path: '/yards', moreSubtitle: 'Service locations' },
  { id: 'team', label: 'Team', icon: 'Users', section: 'OPERATIONS', surfaces: ['sidebar', 'more'], path: '/team', moreSubtitle: 'LIVE_TEAM_COUNT' },
  // The office tree's only way onto the clock. ClockInScreen was routed at
  // /payroll/clock-in with nothing linking to it, so an admin or dispatcher who
  // works hours could not clock in (every other clock-in link lives in the field
  // trees). Clock in/out carry no role gate at the API.
  //
  // Ungated here on purpose, and still limited to office roles: this registry
  // renders only inside AppLayout, which AppRoutes hands to admin, office_staff,
  // supervisor, dispatcher and logistics. field_worker and driver get a field
  // tree with its own clock action; customer gets the portal.
  //
  // It shows no on-the-clock state. The office shell does not fetch
  // /timesheets/status — only the screen itself does — so the sidebar would be
  // reading a cache last filled whenever someone last opened the screen, and a
  // stale "on the clock" is worse than no claim at all.
  { id: 'clock-in', label: 'Clock In', icon: 'Clock', section: 'OPERATIONS', surfaces: ['sidebar', 'more'], path: '/payroll/clock-in', moreSubtitle: 'Clock in and out of your shift' },
  { id: 'time-off', label: 'Time Off', icon: 'CalendarDays', section: 'OPERATIONS', surfaces: ['sidebar', 'more'], path: '/time-off', moreSubtitle: 'Request or approve time off' },
  { id: 'payroll', label: 'Payroll', icon: 'DollarSign', section: 'FINANCE', surfaces: ['sidebar', 'more'], path: '/payroll', moreSubtitle: 'LIVE_PAYROLL' },
  // The screen behind /payroll/approvals is the company timesheet LIST — Pending,
  // Approved and Rejected tabs over GET /timesheets — so it is labelled for what it
  // is rather than for the one thing you do on the first tab. It was reachable by
  // typing the URL, or from the shop-time empty state on Payroll Reports, and from
  // nowhere else. That empty state only renders when the shop report is empty, so
  // the app's only way in was conditional on there being nothing to see.
  //
  // Ungated on purpose. There is no way to gate a sidebar entry by role today —
  // `gate` is read by MoreScreen alone — and none is needed: GET /timesheets carries
  // no requireRole and scopes relationally in the service, so a non-approver opening
  // this gets a correct screen showing their own hours, with the Approve buttons
  // replaced by a notice that says why. Approve/reject are gated at the API.
  { id: 'timesheet-approvals', label: 'Timesheets', icon: 'ClipboardCheck', section: 'FINANCE', surfaces: ['sidebar', 'more'], path: '/payroll/approvals', moreSubtitle: 'Review and approve hours' },
  // Receipts hands file from the field, awaiting approval. Ungated for the
  // same reason as Timesheets above: approve/reject are gated at the API, and a
  // 403 on the list renders a permission notice rather than an empty state.
  { id: 'expenses', label: 'Expenses', icon: 'ReceiptText', section: 'FINANCE', surfaces: ['sidebar', 'more'], path: '/expenses', moreSubtitle: 'Receipts and approvals' },
  // The report half of the Timesheets screen. It was its own page, "Payroll
  // Reports" at /payroll/reports; every tab aggregates the timesheets clock rows,
  // so it now lives beside that list and the old route redirects here. Kept as its
  // own entry because the casing sidebar dropped it (#680) and More is the only way
  // a phone reaches it — a report nobody can find is the Equipment Maintenance
  // failure again. Still on the trucking/rentals sidebars through
  // buildCasingSidebarGroups, where it was before.
  { id: 'timesheet-reports', label: 'Timesheet reports', icon: 'BarChart3', section: 'FINANCE', surfaces: ['sidebar', 'more'], path: '/payroll/approvals?view=reports', moreSubtitle: 'Hours, OT, cost by job, shop time' },
  { id: 'quotes', label: 'Quotes', icon: 'FileText', section: 'FINANCE', surfaces: ['sidebar', 'more'], path: '/quotes', moreSubtitle: 'Create & send quotes' },
  { id: 'invoices', label: 'Invoices', icon: 'Receipt', section: 'FINANCE', surfaces: ['sidebar', 'more'], path: '/finance/invoices', morePath: '/invoices', moreSubtitle: 'Generate & manage invoices' },
  { id: 'clients', label: 'Clients', icon: 'Building2', section: 'OPERATIONS', surfaces: ['sidebar', 'more'], path: '/customers', moreSubtitle: 'Manage clients' },
  { id: 'rates', label: 'Rates', moreLabel: 'Rate Sheets', icon: 'FileText', section: 'FINANCE', surfaces: ['sidebar', 'more'], path: '/rates', moreSubtitle: 'LIVE_RATE_SHEET_COUNT' },
  { id: 'reports', label: 'Reports', icon: 'BarChart3', section: 'REPORTS', surfaces: ['sidebar', 'more'], path: '/reports', moreSubtitle: 'View analytics' },
  { id: 'cert-review', label: 'Cert Review', icon: 'ShieldCheck', section: 'OPERATIONS', surfaces: ['more'], path: '/cert-review', moreSubtitle: 'Review pending certifications', gate: 'isReviewer' },
  { id: 'settings', label: 'Settings', icon: 'Settings', section: 'SYSTEM', surfaces: ['sidebar', 'more'], path: '/settings', moreSubtitle: 'Company, users, integrations' },
  // The CRM (sales-log D11). Drawn for CRM_ROLES only — every consumer filters
  // through navPathOpenForRole, which refuses a /sales path to any other role
  // (lib/sales-access.ts isCrmPath). No vertical guard: the CRM is cross-vertical.
  { id: 'sales-meetings', label: 'Meetings', icon: 'Handshake', section: 'SALES', surfaces: ['sidebar', 'more'], path: '/sales/meetings', moreSubtitle: 'Log meetings and follow-ups' },
  { id: 'sales-pipeline', label: 'Pipeline', icon: 'TrendingUp', section: 'SALES', surfaces: ['sidebar', 'more'], path: '/sales/pipeline', moreSubtitle: 'Opportunities by stage' },

  // ── Sidebar-only items (order fixed by SIDEBAR_ORDER, not array position) ──
  { id: 'training', label: 'Training', icon: 'GraduationCap', section: 'OPERATIONS', surfaces: ['sidebar'], path: '/training' },
  { id: 'ai-agent', label: 'AI Agent', icon: 'Bot', section: 'SYSTEM', surfaces: ['sidebar'], path: '/agent' },
  { id: 'alerts', label: 'Alerts', icon: 'Bell', section: 'SYSTEM', surfaces: ['sidebar'], path: '/alerts' },
];

/** Bottom bar items, sorted by bottomBarOrder. "More" is appended by BottomNav itself. */
export function getCasingBottomBarItems(): CasingNavItem[] {
  return CASING_NAV
    .filter((i) => i.surfaces.includes('bottomBar'))
    .sort((a, b) => (a.bottomBarOrder ?? 99) - (b.bottomBarOrder ?? 99));
}

/** More-page items, in array (More) order. */
export function getCasingMoreItems(): CasingNavItem[] {
  return CASING_NAV.filter((i) => i.surfaces.includes('more'));
}

/**
 * Sidebar items grouped by section (in SIDEBAR_SECTION_ORDER), each group's items
 * sorted by SIDEBAR_ORDER. Empty groups are omitted.
 */
export function buildCasingSidebarGroups(): { section: NavSection; items: CasingNavItem[] }[] {
  const orderIndex = (id: string) => {
    const idx = SIDEBAR_ORDER.indexOf(id);
    return idx === -1 ? Number.MAX_SAFE_INTEGER : idx;
  };
  return SIDEBAR_SECTION_ORDER.map((section) => ({
    section,
    items: CASING_NAV
      .filter((i) => i.surfaces.includes('sidebar') && i.section === section)
      .sort((a, b) => orderIndex(a.id) - orderIndex(b.id)),
  })).filter((g) => g.items.length > 0);
}

/* ────────────── casing sidebar structure ────────────── */

/**
 * The grouped sidebar the CASING vertical renders. Deliberately a separate,
 * explicit list rather than a re-sort of `section` above, for two reasons:
 *
 *  1. The `section` field also drives the sidebar for TRUCKING and SURFACE RENTALS
 *     tenants, who render CASING_NAV's non-CORE sections as their shared groups.
 *     Re-sectioning items in place would silently restructure their nav too, and
 *     only casing is changing here.
 *  2. Membership is now a whitelist. An item absent from this list simply does not
 *     render in the casing sidebar — its route, its More entry and its bottom-bar
 *     entry are all untouched. That is how Training, Timesheet reports and AI Agent
 *     leave the sidebar without becoming 404s.
 *
 * `label` overrides the registry label for the sidebar only, so the bottom bar keeps
 * its short labels ("Tickets" fits a 5-up bar; "Field Tickets" does not).
 */
export type CasingSidebarSection = 'FAVORITES' | 'OPERATIONS' | 'SALES' | 'REPORTS' | 'FINANCE' | 'SYSTEM';

export interface CasingSidebarGroupSpec {
  section: CasingSidebarSection;
  /** Starting state. Groups collapse independently and the user's choice wins after that. */
  defaultCollapsed: boolean;
  items: { id: string; label?: string }[];
}

export const CASING_SIDEBAR_STRUCTURE: CasingSidebarGroupSpec[] = [
  {
    section: 'FAVORITES',
    defaultCollapsed: false,
    items: [
      { id: 'home', label: 'Overview' },
      { id: 'intake' },
      { id: 'jobs' },
      { id: 'dispatch' },
      { id: 'tickets', label: 'Field Tickets' },
    ],
  },
  {
    section: 'OPERATIONS',
    defaultCollapsed: false,
    items: [
      { id: 'equipment' },
      { id: 'team' },
      { id: 'clock-in' },
      { id: 'time-off' },
      { id: 'safety' },
    ],
  },
  {
    // The CRM (D11, approved). Clients moved here from OPERATIONS in CRM-FE-3:
    // the customer list is the CRM's account list now. For a casing tenant only
    // — this whitelist is not what trucking/rentals build from (verticalNav.ts
    // keeps Clients under their COMPANY row), and the registry's `section` for
    // `clients` is unchanged so the legacy sidebar keeps it under OPERATIONS.
    section: 'SALES',
    defaultCollapsed: false,
    items: [{ id: 'sales-meetings' }, { id: 'sales-pipeline' }, { id: 'clients' }],
  },
  {
    section: 'REPORTS',
    defaultCollapsed: false,
    items: [{ id: 'reports' }],
  },
  {
    section: 'FINANCE',
    defaultCollapsed: true,
    items: [
      // The registry calls this one "Rates" in the sidebar and "Rate Sheets" in More.
      // The screen behind it is RateSheetsScreen, so the longer name is the true one.
      { id: 'rates', label: 'Rate Sheets' },
      { id: 'quotes' },
      { id: 'invoices' },
      { id: 'expenses' },
      { id: 'payroll' },
      { id: 'timesheet-approvals', label: 'Timesheets' },
    ],
  },
  {
    section: 'SYSTEM',
    defaultCollapsed: true,
    items: [{ id: 'alerts' }, { id: 'settings' }],
  },
];

/**
 * Resolve CASING_SIDEBAR_STRUCTURE against the registry.
 *
 * Throws on an id that is not in CASING_NAV rather than dropping it. A typo'd id and a
 * deliberately-omitted item are otherwise the same thing — an absent nav entry — and an
 * absent nav entry can only be reached by someone who already knows the URL, which is the
 * failure this whole config exists to prevent.
 */
export function buildCasingSidebarStructure(): {
  section: CasingSidebarSection;
  defaultCollapsed: boolean;
  items: (CasingNavItem & { label: string })[];
}[] {
  const byId = new Map(CASING_NAV.map((i) => [i.id, i]));
  return CASING_SIDEBAR_STRUCTURE.map((group) => ({
    section: group.section,
    defaultCollapsed: group.defaultCollapsed,
    items: group.items.map((entry) => {
      const item = byId.get(entry.id);
      if (!item) {
        throw new Error(
          `casingNav: CASING_SIDEBAR_STRUCTURE references unknown nav id "${entry.id}"`,
        );
      }
      return { ...item, label: entry.label ?? item.label };
    }),
  }));
}
